'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { initSentry } from '@/lib/sentry';
import { initPosthog, shutdownPosthog } from '@/lib/posthog';
import { initGoogleTag } from '@/lib/google-tag';
import { useConsent } from '@/lib/consent';

/**
 * Initialises Sentry + PostHog + Google Tag when the user has accepted
 * analytics cookies. Sentry-only would be defensible too (errors aren't
 * personally identifying after PII redaction), but the simplest legible
 * story is "no telemetry until you opt in" — flip this if you want to
 * split.
 *
 * The Google Tag is *also* gated on path: admin / operator pages
 * (`/admin/*`) intentionally never load it so internal traffic doesn't
 * pollute marketing analytics. Once gtag.js is on the page it can't be
 * cleanly unloaded mid-session, so guarding at init time is the only
 * reliable point.
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { consent } = useConsent();
  const pathname = usePathname();
  const isAdminRoute = pathname?.startsWith('/admin') ?? false;

  useEffect(() => {
    if (consent === 'accepted') {
      initSentry();
      initPosthog();
      if (!isAdminRoute) {
        initGoogleTag();
      }
    } else if (consent === 'declined') {
      shutdownPosthog();
      // Google Tag has no opt-out toggle of its own — the script just
      // never gets injected when consent isn't 'accepted'. The user
      // clearing cookies + reloading is the proper reset, which the
      // consent banner copy already explains.
    }
  }, [consent, isAdminRoute]);

  return <>{children}</>;
}
